import type { MappingReferenceData } from 'services/reference-generator/types';
import type { ReviewStatus } from 'store/useReviewStore';

/** The fields the review rules read on a mapping row: provenance, status and when it last changed. */
export type MappingFlagged = Pick<MappingReferenceData, 'created_source' | 'status' | 'updated_at'>;

/** Review status of one mapping row: AI rows carry their own (absent means accepted), every other row is accepted. */
export const mappingRowStatus = (ref: MappingFlagged): ReviewStatus =>
  ref.created_source === 'ai' ? (ref.status ?? 'accepted') : 'accepted';

/** Rows Save and manual edits may change: human rows and accepted AI rows. Pending and dismissed AI rows belong to review. */
export const isEditableMappingReference = (ref: MappingFlagged): boolean => mappingRowStatus(ref) === 'accepted';

/** The cell's reference: its first editable row in server order (newest first; unsaved picks sit in front). */
export const activeMappingReference = <T extends MappingFlagged>(refs: T[] | undefined): T | undefined =>
  refs?.find(isEditableMappingReference);

/** AI proposals still waiting for a decision, in server order (newest first). */
export const pendingMappingProposals = <T extends MappingFlagged>(refs: T[] | undefined): T[] =>
  (refs ?? []).filter(ref => mappingRowStatus(ref) === 'pending');

// The row with the latest decision time; ties, or any row without a readable time, keep server order.
const latestDecision = <T extends MappingFlagged>(rows: T[]): T | undefined => {
  const times = rows.map(row => Date.parse(row.updated_at ?? ''));
  const latest = times.some(Number.isNaN) ? 0 : times.reduce((best, time, i) => (time > times[best] ? i : best), 0);
  return rows[latest];
};

/**
 * The AI row a review card rules on: the newest pending proposal; else the settled AI row with
 * the latest decision, so the card shows what the analyst decided last and reopen undoes it.
 */
export const pickMappingProposal = <T extends MappingFlagged>(refs: T[] | undefined): T | undefined => {
  const [pending] = pendingMappingProposals(refs);
  return pending ?? latestDecision((refs ?? []).filter(ref => ref.created_source === 'ai'));
};

/**
 * The proposal id a card decision also settles: accept and dismiss take a pending proposal;
 * reopen takes one only when it shows the card's settled status, so it undoes that decision alone.
 */
export const mappingProposalIdsFor = <T extends MappingFlagged & { id?: number | null }>(
  refs: T[] | undefined,
  action: 'accept' | 'dismiss' | 'reopen',
  cardStatus: ReviewStatus
): number[] => {
  const proposal = pickMappingProposal(refs);
  const from = action === 'reopen' ? cardStatus : 'pending';
  return proposal?.id != null && mappingRowStatus(proposal) === from ? [proposal.id] : [];
};
